import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'
import React, { useState, useEffect, memo } from 'react'
import { nanoid } from 'nanoid'
import dayjs from 'dayjs'
import { ReactComponent as LittleArrowBottomArrowIcon } from '../../../../assets/icons/app/bottom.icon.svg'
import GlHDivider from '../../../share/GlHDivider'
import { RequestFiltersPeriodTypesConfig } from '../../../../core/config/dynamics/request-filters-period-types.config'
import { PeriodTypeEnum } from '../../../../core/enums/period-type.enum'
import { ReactComponent as SelectedIcon } from '../../../../assets/icons/selected.icon.svg'
import DynamicsResultsPeriodDateType from './period/DynamicsResultsPeriodDateType'
import {
  PeriodsFiltersContainerStyle,
  PeriodsFiltersLabelStyle,
  PeriodsFiltersMenuStyle,
} from '../../../analytics-details-v2/PeriodsFiltersStyles'

const TitleSelectStyle = styled.div`
  font-family: 'Fira Sans', sans-serif;
  font-style: normal;
  font-weight: 400;
  font-size: 11px;
  line-height: 11px;
  display: flex;
  align-items: center;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #b2b2b2;
  padding: 8px 12px;
`

const DynamicsResultsPeriodsFilters = ({
  periodTypeSelected,
  handleChangeQuery,
  startDateSelected,
  endDateSelected,
  menuKey,
  menuOpen,
  handleMenuOpen,
}: {
  periodTypeSelected: PeriodTypeEnum
  handleChangeQuery: (key: any, value: any) => void
  startDateSelected: string
  endDateSelected: string
  menuKey: string
  menuOpen: string | null
  handleMenuOpen: (value: string | null) => void
}) => {
  const { t } = useTranslation()

  let timer: NodeJS.Timeout | null = null
  const [needClose, setNeedClose] = useState<boolean>(false)
  const [hasHover, setHasHover] = useState<boolean>(false)

  const handleTimeOut = () => {
    timer = setTimeout(() => {
      if (menuOpen === menuKey) {
        handleMenuOpen(null)
      }
      setNeedClose(false)
    }, 300)
  }

  useEffect(() => {
    if (needClose && !hasHover) {
      handleTimeOut()
    }

    return () => {
      if (timer) {
        clearTimeout(timer)
      }
    }
  }, [needClose, hasHover, timer, menuOpen])

  const handleMouseEnter = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.preventDefault()
    handleMenuOpen(menuKey)
    setHasHover(true)
  }

  const handleMouseLeave = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
  ) => {
    event.preventDefault()
    setHasHover(false)
    setNeedClose(true)
  }

  return (
    <PeriodsFiltersContainerStyle
      className="flex flex-col relative bg-white catalog-filter"
      width={260}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <PeriodsFiltersLabelStyle className="w-full h-full flex flex-row justify-between items-center catalog-filter">
        <span>
          {t(`webApp.dynamics.topRequest.period.${periodTypeSelected}`, {
            startDate: dayjs(startDateSelected).format('DD/MM/YYYY'),
            endDate: dayjs(endDateSelected).format('DD/MM/YYYY'),
            month: dayjs(startDateSelected).format('MMMM YYYY'),
            quarter: dayjs(startDateSelected).format('Q'),
            year: dayjs(startDateSelected).format('YYYY'),
          })}
        </span>
        <LittleArrowBottomArrowIcon className="text-[#b2b2b2]" />
      </PeriodsFiltersLabelStyle>
      <PeriodsFiltersMenuStyle
        open={menuOpen === menuKey}
        className="shadow-2xl z-45 catalog-filter"
      >
        <div className="catalog-filter">
          <TitleSelectStyle>PÉRIODE</TitleSelectStyle>
          <GlHDivider />
          {Object.keys(RequestFiltersPeriodTypesConfig).map((keyValue) => (
            <div
              key={nanoid(5)}
              className={`
                flex
                justify-between
                h-7
                items-center
                px-3
                py-[4.5px]
                ${
                  (periodTypeSelected ===
                    RequestFiltersPeriodTypesConfig[keyValue as PeriodTypeEnum]
                      .value &&
                    'bg-[#e3e3e3]') ||
                  ''
                }
                hover:bg-[#f7f7f7]
                active:bg-[#e3e3e3]
              `}
              onClick={(event: React.MouseEvent<HTMLDivElement>) => {
                event.preventDefault()
                handleChangeQuery(
                  'period',
                  RequestFiltersPeriodTypesConfig[keyValue as PeriodTypeEnum]
                    .value,
                )
              }}
            >
              <span>
                {
                  RequestFiltersPeriodTypesConfig[keyValue as PeriodTypeEnum]
                    .label
                }
              </span>
              {periodTypeSelected ===
                RequestFiltersPeriodTypesConfig[keyValue as PeriodTypeEnum]
                  .value && <SelectedIcon />}
            </div>
          ))}
        </div>
        <GlHDivider />
        <div className="catalog-filter">
          <TitleSelectStyle>DATE</TitleSelectStyle>
          <GlHDivider />
          <DynamicsResultsPeriodDateType
            menuIsOpen={menuOpen === menuKey}
            periodTypeSelected={periodTypeSelected}
            startDateSelected={startDateSelected}
            endDateSelected={endDateSelected}
            handleChangeQuery={handleChangeQuery}
          />
        </div>
      </PeriodsFiltersMenuStyle>
    </PeriodsFiltersContainerStyle>
  )
}

export default memo(DynamicsResultsPeriodsFilters)
